/**
 * Scenario Status Visual Styles
 *
 * Defines visual styling for nodes based on their scenario status.
 * Used to render visual diff indicators on org chart nodes.
 *
 * Color conventions follow industry standards:
 * - Added: Green (#22C55E)
 * - Removed: Red (#EF4444) with dashed border and reduced opacity
 * - Modified: Amber (#F59E0B)
 * - Moved: Blue (#3B82F6)
 */
import type { NodeScenarioStatus } from '../models/Scenario';

export interface ScenarioStatusStyle {
  /** Background color for the node */
  backgroundColor: string;
  /** Border/stroke color */
  borderColor: string;
  /** Border width */
  borderWidth: number;
  /** Whether border is dashed */
  borderDashed: boolean;
  /** Opacity of the entire node */
  opacity: number;
  /** Badge background color */
  badgeBackground: string;
  /** Badge text color */
  badgeTextColor: string;
  /** Icon character to display */
  icon: string;
  /** Human-readable label */
  label: string;
}

/** Style definitions for each scenario status */
export const SCENARIO_STATUS_STYLES: Record<NodeScenarioStatus, ScenarioStatusStyle> = {
  added: {
    backgroundColor: '#F0FDF4', // green-50
    borderColor: '#4ADE80', // green-400
    borderWidth: 2,
    borderDashed: false,
    opacity: 1,
    badgeBackground: '#DCFCE7', // green-100
    badgeTextColor: '#166534', // green-800
    icon: '+',
    label: 'Added',
  },
  removed: {
    backgroundColor: '#FEF2F2', // red-50
    borderColor: '#F87171', // red-400
    borderWidth: 2,
    borderDashed: true,
    opacity: 0.6,
    badgeBackground: '#FEE2E2', // red-100
    badgeTextColor: '#991B1B', // red-800
    icon: '−',
    label: 'Removed',
  },
  modified: {
    backgroundColor: '#FFFBEB', // amber-50
    borderColor: '#FBBF24', // amber-400
    borderWidth: 2,
    borderDashed: false,
    opacity: 1,
    badgeBackground: '#FEF3C7', // amber-100
    badgeTextColor: '#92400E', // amber-800
    icon: '~',
    label: 'Modified',
  },
  moved: {
    backgroundColor: '#EFF6FF', // blue-50
    borderColor: '#60A5FA', // blue-400
    borderWidth: 2,
    borderDashed: false,
    opacity: 1,
    badgeBackground: '#DBEAFE', // blue-100
    badgeTextColor: '#1E40AF', // blue-800
    icon: '↔',
    label: 'Moved',
  },
  unchanged: {
    backgroundColor: '', // Use node's default
    borderColor: '',
    borderWidth: 0,
    borderDashed: false,
    opacity: 1,
    badgeBackground: '',
    badgeTextColor: '',
    icon: '',
    label: '',
  },
};

/**
 * Get the visual style for a given scenario status.
 */
export function getScenarioStatusStyle(status: NodeScenarioStatus | undefined): ScenarioStatusStyle {
  return SCENARIO_STATUS_STYLES[status ?? 'unchanged'];
}

/**
 * Check if a node should show scenario styling.
 */
export function shouldShowScenarioStyling(status: NodeScenarioStatus | undefined): boolean {
  return status !== undefined && status !== 'unchanged';
}

/**
 * Get Konva dash array for dashed borders.
 */
export function getKonvaDash(status: NodeScenarioStatus | undefined): number[] {
  if (!status) return [];
  const style = SCENARIO_STATUS_STYLES[status];
  if (style.borderDashed) {
    return [8, 4]; // 8px dash, 4px gap
  }
  return [];
}

/**
 * Get border/stroke color, falling back to selected color or default.
 */
export function getScenarioStrokeColor(
  status: NodeScenarioStatus | undefined,
  selected: boolean,
  defaultSelectedColor = '#3B82F6'
): string {
  const style = SCENARIO_STATUS_STYLES[status ?? 'unchanged'];

  // Scenario status takes precedence over selection
  if (shouldShowScenarioStyling(status)) {
    return style.borderColor;
  }

  // Otherwise use selection color
  return selected ? defaultSelectedColor : 'transparent';
}

/**
 * Get border/stroke width.
 */
export function getScenarioStrokeWidth(
  status: NodeScenarioStatus | undefined,
  selected: boolean,
  defaultSelectedWidth = 2
): number {
  const style = SCENARIO_STATUS_STYLES[status ?? 'unchanged'];

  if (shouldShowScenarioStyling(status)) {
    return style.borderWidth;
  }

  return selected ? defaultSelectedWidth : 0;
}

/**
 * Get fill color, optionally blending with scenario background.
 */
export function getScenarioFillColor(
  status: NodeScenarioStatus | undefined,
  defaultFillColor: string
): string {
  const style = SCENARIO_STATUS_STYLES[status ?? 'unchanged'];

  if (shouldShowScenarioStyling(status) && style.backgroundColor) {
    return style.backgroundColor;
  }

  return defaultFillColor;
}

/**
 * Get node opacity based on scenario status.
 */
export function getScenarioOpacity(status: NodeScenarioStatus | undefined): number {
  return SCENARIO_STATUS_STYLES[status ?? 'unchanged'].opacity;
}
